import React, { useState, useEffect, useRef } from 'react'
import { ArrowLeft, ArrowRight, Search, Building, Calendar, Clock, Baby, CheckCircle, AlertTriangle, X, Repeat } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'

interface Center {
  id: string
  name: string
}

interface SessionResult {
  id: string
  stage_id: string
  center_id: string
  periode: string
  semaine: string
  start_date: string
  end_date: string
  capacity: number
  current_registrations: number
  archived: boolean
  stage: { id: string; title: string }
  center: { id: string; name: string }
}

interface RegistrationResult {
  id: string
  kid_id: string
  activity_id: string
  payment_status: string
  kid: {
    id: string
    nom: string
    prenom: string
    date_naissance: string
  }
  user: {
    nom: string
    prenom: string
    email: string
  }
}

interface Props {
  onBack: () => void
  onNavigateDashboard?: () => void
}

export function GroupTransfer({ onBack, onNavigateDashboard }: Props) {
  const { userProfile } = useAuth()

  // Source state
  const [srcCenters, setSrcCenters] = useState<Center[]>([])
  const [srcCenter, setSrcCenter] = useState('')
  const [srcPeriods, setSrcPeriods] = useState<string[]>([])
  const [srcPeriod, setSrcPeriod] = useState('')
  const [srcWeeks, setSrcWeeks] = useState<string[]>([])
  const [srcWeek, setSrcWeek] = useState('')
  const [srcSessions, setSrcSessions] = useState<SessionResult[]>([])
  const [srcSession, setSrcSession] = useState('')
  const [srcRegistrations, setSrcRegistrations] = useState<RegistrationResult[]>([])
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationResult | null>(null)
  const [srcSearch, setSrcSearch] = useState('')
  const [loadingSrcRegs, setLoadingSrcRegs] = useState(false)

  // Destination state
  const [dstCenter, setDstCenter] = useState('')
  const [dstPeriods, setDstPeriods] = useState<string[]>([])
  const [dstPeriod, setDstPeriod] = useState('')
  const [dstWeeks, setDstWeeks] = useState<string[]>([])
  const [dstWeek, setDstWeek] = useState('')
  const [dstSessions, setDstSessions] = useState<SessionResult[]>([])
  const [selectedDstSession, setSelectedDstSession] = useState<SessionResult | null>(null)

  // Transfer state
  const [showConfirm, setShowConfirm] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [successInfo, setSuccessInfo] = useState<{ kidName: string; fromGroup: string; toGroup: string } | null>(null)
  const [errorMessage, setErrorMessage] = useState('')

  // Load centers on mount
  useEffect(() => {
    const fetchCenters = async () => {
      const { data } = await supabase
        .from('centers')
        .select('id, name')
        .eq('active', true)
        .order('name')
      setSrcCenters(data || [])
    }
    fetchCenters()
  }, [])

  // Source: load periods when center changes
  useEffect(() => {
    setSrcPeriod('')
    setSrcWeek('')
    setSrcSession('')
    setSrcSessions([])
    setSrcRegistrations([])
    setSelectedRegistration(null)
    if (!srcCenter) { setSrcPeriods([]); return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('periode')
        .eq('center_id', srcCenter)
        .eq('archived', false), await stagesDesJpa())
      const unique = [...new Set((data || []).map(s => s.periode))].sort()
      setSrcPeriods(unique)
    }
    fetch()
  }, [srcCenter])

  // Source: load weeks when period changes
  useEffect(() => {
    setSrcWeek('')
    setSrcSession('')
    setSrcSessions([])
    setSrcRegistrations([])
    setSelectedRegistration(null)
    if (!srcCenter || !srcPeriod) { setSrcWeeks([]); return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('semaine')
        .eq('center_id', srcCenter)
        .eq('periode', srcPeriod)
        .eq('archived', false), await stagesDesJpa())
      const unique = [...new Set((data || []).map(s => s.semaine))].sort()
      setSrcWeeks(unique)
    }
    fetch()
  }, [srcCenter, srcPeriod])

  // Source: load sessions when week changes
  useEffect(() => {
    setSrcSession('')
    setSrcRegistrations([])
    setSelectedRegistration(null)
    if (!srcCenter || !srcPeriod || !srcWeek) { setSrcSessions([]); return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select(`
          id, stage_id, center_id, periode, semaine, start_date, end_date,
          capacity, current_registrations, archived,
          stage:stages(id, title),
          center:centers(id, name)
        `)
        .eq('center_id', srcCenter)
        .eq('periode', srcPeriod)
        .eq('semaine', srcWeek)
        .eq('archived', false)
        .order('start_date'), await stagesDesJpa())
      setSrcSessions((data as any) || [])
    }
    fetch()
  }, [srcCenter, srcPeriod, srcWeek])

  // Source: load registrations when session is selected
  useEffect(() => {
    setSelectedRegistration(null)
    if (!srcSession) { setSrcRegistrations([]); return }

    const fetch = async () => {
      setLoadingSrcRegs(true)
      const { data } = await supabase
        .from('registrations')
        .select(`
          id, kid_id, activity_id, payment_status,
          kid:kids(id, nom, prenom, date_naissance),
          user:users(nom, prenom, email)
        `)
        .eq('activity_id', srcSession)
        .eq('cancellation_status', 'none')
        .order('kid(nom)')
      setSrcRegistrations((data as any) || [])
      setLoadingSrcRegs(false)
    }
    fetch()
  }, [srcSession])

  // Pre-fill destination with same center/period/week when a child is selected
  const prefillRef = useRef(false)
  useEffect(() => {
    if (selectedRegistration && srcCenter && srcPeriod && srcWeek) {
      prefillRef.current = true
      setDstCenter(srcCenter)
      setDstPeriod(srcPeriod)
      setDstWeek(srcWeek)
    }
  }, [selectedRegistration])

  // Destination: load periods when center changes
  useEffect(() => {
    if (prefillRef.current) {
      // Don't reset period/week during a pre-fill cascade
    } else {
      setDstPeriod('')
      setDstWeek('')
    }
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter) { setDstPeriods([]); return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('periode')
        .eq('center_id', dstCenter)
        .eq('archived', false), await stagesDesJpa())
      const unique = [...new Set((data || []).map(s => s.periode))].sort()
      setDstPeriods(unique)
    }
    fetch()
  }, [dstCenter])

  // Destination: load weeks when period changes
  useEffect(() => {
    if (prefillRef.current) {
      // Don't reset week during a pre-fill cascade
    } else {
      setDstWeek('')
    }
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter || !dstPeriod) { setDstWeeks([]); return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('semaine')
        .eq('center_id', dstCenter)
        .eq('periode', dstPeriod)
        .eq('archived', false), await stagesDesJpa())
      const unique = [...new Set((data || []).map(s => s.semaine))].sort()
      setDstWeeks(unique)
    }
    fetch()
  }, [dstCenter, dstPeriod])

  // Destination: load sessions when week changes
  useEffect(() => {
    prefillRef.current = false
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter || !dstPeriod || !dstWeek) { return }

    const fetch = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select(`
          id, stage_id, center_id, periode, semaine, start_date, end_date,
          capacity, current_registrations, archived,
          stage:stages(id, title),
          center:centers(id, name)
        `)
        .eq('center_id', dstCenter)
        .eq('periode', dstPeriod)
        .eq('semaine', dstWeek)
        .eq('archived', false)
        .order('start_date'), await stagesDesJpa())
      setDstSessions((data as any) || [])
    }
    fetch()
  }, [dstCenter, dstPeriod, dstWeek])

  const filteredSrcRegistrations = srcRegistrations.filter(reg => {
    if (!srcSearch) return true
    const search = srcSearch.toLowerCase()
    return (
      reg.kid.nom.toLowerCase().includes(search) ||
      reg.kid.prenom.toLowerCase().includes(search) ||
      reg.user.nom.toLowerCase().includes(search) ||
      reg.user.email.toLowerCase().includes(search)
    )
  })

  const canTransfer = selectedRegistration && selectedDstSession && selectedDstSession.id !== srcSession

  const handleTransfer = async () => {
    if (!selectedRegistration || !selectedDstSession) return

    setTransferring(true)
    setErrorMessage('')
    try {
      // Check the kid is not already registered in the destination session
      const { data: existing } = await supabase
        .from('registrations')
        .select('id')
        .eq('kid_id', selectedRegistration.kid_id)
        .eq('activity_id', selectedDstSession.id)
        .eq('cancellation_status', 'none')
        .limit(1)

      if (existing && existing.length > 0) {
        setErrorMessage('Cet enfant est deja inscrit dans la session de destination.')
        setTransferring(false)
        setShowConfirm(false)
        return
      }

      // Check capacity
      if (selectedDstSession.current_registrations >= selectedDstSession.capacity) {
        setErrorMessage('La session de destination est pleine.')
        setTransferring(false)
        setShowConfirm(false)
        return
      }

      // Perform transfer: update registration.activity_id, reset subgroup
      const { error: updateError } = await supabase
        .from('registrations')
        .update({
          activity_id: selectedDstSession.id,
          subgroup_id: null,
          subgroup_name: null
        })
        .eq('id', selectedRegistration.id)

      if (updateError) throw updateError

      // Decrement source session count
      await supabase
        .from('sessions')
        .update({ current_registrations: Math.max(0, (srcSessions.find(s => s.id === srcSession)?.current_registrations || 1) - 1) })
        .eq('id', srcSession)

      // Increment destination session count
      await supabase
        .from('sessions')
        .update({ current_registrations: selectedDstSession.current_registrations + 1 })
        .eq('id', selectedDstSession.id)

      // Record transfer in history
      await supabase
        .from('registration_transfers')
        .insert({
          registration_id: selectedRegistration.id,
          kid_id: selectedRegistration.kid_id,
          from_session_id: srcSession,
          to_session_id: selectedDstSession.id,
          transferred_by: userProfile?.id
        })

      const kidName = `${selectedRegistration.kid.prenom} ${selectedRegistration.kid.nom}`
      const fromGroup = srcSessionData?.stage?.title || ''
      const toGroup = selectedDstSession.stage?.title || ''

      setShowConfirm(false)
      setSuccessInfo({ kidName, fromGroup, toGroup })
      setShowSuccess(true)
    } catch (err) {
      console.error('Transfer error:', err)
      setErrorMessage('Erreur lors du transfert. Veuillez reessayer.')
    } finally {
      setTransferring(false)
    }
  }

  const calculateAge = (birthDate: string) => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
    return age
  }

  const resetForm = () => {
    setSrcCenter('')
    setSrcPeriod('')
    setSrcWeek('')
    setSrcSession('')
    setSrcSessions([])
    setSrcRegistrations([])
    setSelectedRegistration(null)
    setSrcSearch('')
    setDstCenter('')
    setDstPeriod('')
    setDstWeek('')
    setDstSessions([])
    setSelectedDstSession(null)
    setErrorMessage('')
    setSuccessInfo(null)
  }

  const handleCloseSuccess = () => {
    setShowSuccess(false)
    resetForm()
  }

  const srcSessionData = srcSessions.find(s => s.id === srcSession)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transfert de groupe</h1>
          <p className="text-gray-600">Transferer un enfant d'une session vers une autre</p>
        </div>
      </div>

      {/* Success overlay */}
      {showSuccess && successInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={handleCloseSuccess}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 flex flex-col items-center text-center animate-[scaleIn_0.3s_ease-out]"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative mb-5">
              <svg className="h-20 w-20" viewBox="0 0 80 80">
                <circle
                  cx="40" cy="40" r="36"
                  fill="none" stroke="#d1fae5" strokeWidth="6"
                />
                <circle
                  cx="40" cy="40" r="36"
                  fill="none" stroke="#10b981" strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray="226"
                  strokeDashoffset="226"
                  className="animate-[circleDraw_0.5s_0.2s_ease-out_forwards]"
                />
                <path
                  d="M24 42 L35 53 L56 30"
                  fill="none" stroke="#10b981" strokeWidth="5"
                  strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="50"
                  strokeDashoffset="50"
                  className="animate-[checkDraw_0.4s_0.6s_ease-out_forwards]"
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Transfert effectue</h3>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-semibold text-gray-900">{successInfo.kidName}</span>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {successInfo.fromGroup} <span className="mx-1">&rarr;</span> {successInfo.toGroup}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCloseSuccess}
                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                Nouveau transfert
              </button>
              {onNavigateDashboard && (
                <button
                  onClick={onNavigateDashboard}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Tableau de bord
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
          <button onClick={() => setErrorMessage('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
              <Baby className="h-5 w-5" />
              Source - Enfant a transferer
            </h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Center select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
              <select
                value={srcCenter}
                onChange={e => setSrcCenter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selectionner un centre</option>
                {srcCenters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Period select */}
            {srcCenter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Periode</label>
                <select
                  value={srcPeriod}
                  onChange={e => setSrcPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selectionner une periode</option>
                  {srcPeriods.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Week select */}
            {srcPeriod && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Semaine</label>
                <select
                  value={srcWeek}
                  onChange={e => setSrcWeek(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selectionner une semaine</option>
                  {srcWeeks.map(w => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Session select */}
            {srcWeek && srcSessions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session (stage)</label>
                <select
                  value={srcSession}
                  onChange={e => setSrcSession(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selectionner une session</option>
                  {srcSessions.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.stage?.title} ({s.current_registrations}/{s.capacity})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Registrations list */}
            {srcSession && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Enfants inscrits</label>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {srcRegistrations.length}
                  </span>
                </div>

                {srcRegistrations.length > 5 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un enfant..."
                      value={srcSearch}
                      onChange={e => setSrcSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {loadingSrcRegs ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {filteredSrcRegistrations.length === 0 ? (
                      <p className="text-sm text-gray-500 p-4 text-center">Aucun enfant trouve</p>
                    ) : (
                      filteredSrcRegistrations.map(reg => (
                        <button
                          key={reg.id}
                          onClick={() => setSelectedRegistration(reg)}
                          className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-blue-50 transition-colors ${
                            selectedRegistration?.id === reg.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {reg.kid.prenom} {reg.kid.nom}
                            </p>
                            <p className="text-xs text-gray-500">
                              {calculateAge(reg.kid.date_naissance)} ans - Parent: {reg.user.prenom} {reg.user.nom}
                            </p>
                          </div>
                          {selectedRegistration?.id === reg.id && (
                            <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Destination Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Destination - Session cible
            </h2>
          </div>
          <div className="p-6 space-y-4">
            {selectedRegistration && dstCenter === srcCenter && dstPeriod === srcPeriod && dstWeek === srcWeek && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                Meme centre et semaine pre-selectionnes. Choisissez le nouveau groupe ci-dessous, ou modifiez les criteres pour un transfert vers un autre centre/semaine.
              </div>
            )}
            {/* Center select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
              <select
                value={dstCenter}
                onChange={e => setDstCenter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Selectionner un centre</option>
                {srcCenters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Period select */}
            {dstCenter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Periode</label>
                <select
                  value={dstPeriod}
                  onChange={e => setDstPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Selectionner une periode</option>
                  {dstPeriods.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Week select */}
            {dstPeriod && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Semaine</label>
                <select
                  value={dstWeek}
                  onChange={e => setDstWeek(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Selectionner une semaine</option>
                  {dstWeeks.map(w => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Available sessions */}
            {dstWeek && dstSessions.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">Sessions disponibles</label>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {dstSessions
                    .filter(s => s.id !== srcSession)
                    .map(session => {
                      const isFull = session.current_registrations >= session.capacity
                      return (
                        <button
                          key={session.id}
                          onClick={() => !isFull && setSelectedDstSession(session)}
                          disabled={isFull}
                          className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                            isFull
                              ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                              : selectedDstSession?.id === session.id
                                ? 'bg-amber-50 border-l-4 border-l-amber-600'
                                : 'hover:bg-amber-50'
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {session.stage?.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {session.start_date} au {session.end_date}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              isFull ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {session.current_registrations}/{session.capacity}
                            </span>
                            {selectedDstSession?.id === session.id && (
                              <CheckCircle className="h-4 w-4 text-amber-600 mt-1 ml-auto" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                </div>
              </div>
            )}

            {dstWeek && dstSessions.filter(s => s.id !== srcSession).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">Aucune session disponible pour cette selection.</p>
            )}
          </div>
        </div>
      </div>

      {/* Transfer button */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!canTransfer}
          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-medium text-sm transition-all ${
            canTransfer
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Repeat className="h-4 w-4" />
          Transferer l'enfant
        </button>
      </div>

      {/* Confirmation modal */}
      {showConfirm && selectedRegistration && selectedDstSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-5">
            <h3 className="text-lg font-semibold text-gray-900">Confirmer le transfert</h3>

            <div className="space-y-3 text-sm">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600 font-medium mb-1">Enfant</p>
                <p className="font-medium text-blue-900">{selectedRegistration.kid.prenom} {selectedRegistration.kid.nom}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium mb-1">De</p>
                  <p className="font-medium text-gray-900">{srcSessionData?.stage?.title}</p>
                  <p className="text-xs text-gray-500">{srcSessionData?.center?.name} - {srcSessionData?.semaine}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-amber-600 font-medium mb-1">Vers</p>
                  <p className="font-medium text-amber-900">{selectedDstSession.stage?.title}</p>
                  <p className="text-xs text-gray-500">{selectedDstSession.center?.name} - {selectedDstSession.semaine}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {transferring ? 'Transfert en cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
